/**
 * Asset
 * Unified Contentful media renderer with network-aware optimizations:
 * - Images: uses AVIF with WebP fallback when unsupported; quality and DPR cap by connection.
 * - Video: disables autoplay and uses preload="none" on slow networks (2G/3G/Save-Data),
 *   shows a play control button in those cases, and optionally derives a poster from the last frame
 *   on fast connections after the video becomes visible (fallbacks to provided poster/image).
 *
 * The goal is to improve Core Web Vitals on constrained networks while preserving fidelity on fast links.
 */
import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Asset as ContentfulAsset } from 'contentful';
import { getImageData } from '../../../utils/contentful';
import Image, { ImageLoaderProps } from 'next/image';
import { useVideo } from '~/hooks/useVideo';
import { captureError, trackRumEvent } from '../../../utils/datadogRum';
import { MediaControlButton } from '../MediaControlButton';
import { useNetworkInfo } from '../../../hooks/useNetworkInfo';
import { useImageFormatSupport } from '../../../hooks/useImageFormatSupport';

interface AssetProps {
  asset: ContentfulAsset;
  videoImageBackup?: ContentfulAsset;
  loading?: 'lazy' | 'eager';
  className?: string;
  style?: React.CSSProperties;
  muted?: boolean;
  alt?: string;
  loop?: boolean;
  controls?: boolean;
  onPlay?: () => void;
  onVideoRef?: (el: HTMLVideoElement | null) => void;
  disableViewportManagement?: boolean;
  /** When true, image fills container with object-fit (for poster overlays) */
  fill?: boolean;
  /** Sizes for responsive image loading (use for both fill and non-fill to match display size and reduce download) */
  sizes?: string;
  /** Object-fit for fill mode */
  objectFit?: 'cover' | 'contain' | 'fill' | 'none';
}

const getQualityForConnection = (
  effectiveType: 'slow-2g' | '2g' | '3g' | '4g' | undefined,
  saveData: boolean
): number => {
  if (saveData) return 50;
  switch (effectiveType) {
    case 'slow-2g':
    case '2g':
      return 55;
    case '3g':
      return 65;
    case '4g':
    default:
      return 75;
  }
};

const DEFAULT_MAX_WIDTH = 850;
const HERO_MAX_WIDTH = 1440;

export const createImageLoader = (
  effectiveType: 'slow-2g' | '2g' | '3g' | '4g' | undefined,
  saveData: boolean,
  devicePixelRatio: number,
  preferredFormat: 'avif' | 'webp' | 'jpg',
  contentType?: string,
  maxWidth: number = DEFAULT_MAX_WIDTH
) => {
  return ({ src, width }: ImageLoaderProps) => {
    const url =
      src.startsWith('http://') || src.startsWith('https://')
        ? src
        : `https:${src}`;

    // SVGs are vector-based and should not be converted
    if (contentType?.includes('svg')) {
      return url;
    }

    const quality = getQualityForConnection(effectiveType, saveData);
    const format = preferredFormat;
    const dprCap = saveData ? 1 : effectiveType === '3g' ? 1.5 : 2;
    // Next.js passes the srcset width; browser picks by DPR. Cap to avoid overserving.
    const adjustedWidth = Math.min(
      Math.ceil(width * Math.min(devicePixelRatio, dprCap)),
      maxWidth
    );

    const params = new URLSearchParams({
      w: adjustedWidth.toString(),
      q: quality.toString(),
      fm: format
    });
    if (format === 'jpg') {
      params.set('fl', 'progressive');
    }

    return `${url}?${params.toString()}`;
  };
};

export const imageLoader = createImageLoader('4g', false, 1, 'avif', undefined);

export const Asset: React.FC<AssetProps> = ({
  asset,
  videoImageBackup,
  alt,
  loading = 'lazy',
  className,
  style,
  muted = true,
  loop = false,
  controls = false,
  onPlay,
  onVideoRef,
  disableViewportManagement = false,
  fill = false,
  sizes: sizesProp,
  objectFit = 'cover'
}) => {
  const networkInfo = useNetworkInfo();
  const preferredFormat = useImageFormatSupport();
  const imageFormat = preferredFormat === 'avif' ? 'avif' : 'webp';
  const description = asset?.fields?.description as string | undefined;
  const title = asset?.fields?.title as string | undefined;
  const altText =
    alt ??
    (description && description.trim() !== ''
      ? description
      : title && title.trim() !== ''
      ? title
      : 'Greenlight image');
  const contentType = asset?.fields?.file?.contentType ?? '';
  const isVideo =
    contentType?.includes('video') || contentType?.includes('mp4');
  const originalUrl = asset?.fields?.file?.url || '';

  const { sourceUrl } = useVideo(originalUrl, {
    manageVisibility: !!isVideo && !disableViewportManagement,
    shouldPlay: !!isVideo && !disableViewportManagement,
    intersectionThreshold: 0.25,
    deferUntilVisible: false
  });

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const autoplayPolicyRef = useRef<string | undefined>(undefined);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [computedPoster, setComputedPoster] = useState<string | undefined>(
    undefined
  );
  const [posterRequested, setPosterRequested] = useState(false);
  const [videoReady, setVideoReady] = useState(false);
  const [hasPlayedOnce, setHasPlayedOnce] = useState(false);
  const [completedOnce, setCompletedOnce] = useState(false);

  const isSlowConnection =
    networkInfo.saveData ||
    networkInfo.effectiveType === 'slow-2g' ||
    networkInfo.effectiveType === '2g' ||
    networkInfo.effectiveType === '3g';

  const resolvedImageLoader = createImageLoader(
    networkInfo.effectiveType,
    networkInfo.saveData,
    networkInfo.devicePixelRatio,
    imageFormat,
    contentType,
    loading === 'eager' ? HERO_MAX_WIDTH : DEFAULT_MAX_WIDTH
  );

  const posterUrl: string | undefined = (() => {
    const raw = videoImageBackup?.fields?.file?.url;
    if (!raw) return undefined;
    const baseUrl = raw.startsWith('http') ? raw : `https:${raw}`;
    if (
      networkInfo.saveData ||
      networkInfo.effectiveType === 'slow-2g' ||
      networkInfo.effectiveType === '2g'
    ) {
      return `${baseUrl}?w=400&q=60&fm=jpg`;
    }
    return baseUrl;
  })();

  const getLqipUrl = (src: string): string => {
    const url =
      src.startsWith('http://') || src.startsWith('https://')
        ? src
        : `https:${src}`;
    return `${url}?w=24&q=20&fm=jpg`;
  };

  // Play/pause based on viewport without IntersectionObserver (broad browser support)
  useEffect(() => {
    if (!isVideo || disableViewportManagement) return;
    if (typeof window === 'undefined') return;
    const node = videoRef.current;
    if (!node) return;

    const getEffectiveAutoplayAllowed = (el: HTMLVideoElement): boolean => {
      const nav = navigator as unknown as {
        getAutoplayPolicy?: (arg: unknown) => string | undefined;
      };
      const policy = nav?.getAutoplayPolicy
        ? nav.getAutoplayPolicy(el) ?? nav.getAutoplayPolicy('mediaelement')
        : undefined;
      return policy !== 'disallowed';
    };

    // Respect reduced motion preferences
    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    );
    if (prefersReducedMotion.matches) {
      node.pause();
      return;
    }

    let ticking = false;
    let rafId = 0;
    let idleCbHandle: number | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;
    let pendingMetadataEl: HTMLVideoElement | null = null;
    let pendingMetadataFn: (() => void) | null = null;

    const isElementInViewport = () => {
      const rect = node.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      if (rect.height === 0) return false;
      const top = Math.max(0, rect.top);
      const bottom = Math.min(viewportHeight, rect.bottom);
      const visibleHeight = Math.max(0, bottom - top);
      const ratio = visibleHeight / rect.height;
      return ratio >= 0.15; // 25% visibility threshold
    };

    let lastVisible = false;
    let wasEndedBeforePause = false;
    let wasPlayingBeforePause = false;

    function playVideo(
      videoEl: HTMLVideoElement,
      restartFromBeginning: boolean
    ) {
      if (!getEffectiveAutoplayAllowed(videoEl)) {
        setAutoplayBlocked(true);
        return;
      }
      // Only restart from beginning if video ended before pause; otherwise resume from current position
      if (restartFromBeginning) {
        videoEl.currentTime = 0;
        setIsEnded(false);
      }
      videoEl
        .play()
        .then(() => {
          setIsPlaying(true);
          setIsEnded(false);
          setAutoplayBlocked(false);
        })
        .catch((err: DOMException) => {
          if (err.name === 'NotAllowedError') {
            setAutoplayBlocked(true);
            trackRumEvent('autoplay_blocked', {
              page_url: window.location.href,
              component: 'Asset',
              video_src: sourceUrl,
              muted: videoEl.muted
            });
            captureError(err);
          }
        });
    }

    const update = () => {
      ticking = false;
      const visible = isElementInViewport();
      if (visible !== lastVisible) {
        lastVisible = visible;
        if (visible) {
          // Ensure muted for autoplay safety
          node.muted = !!muted;
          // Check autoplay policy and override
          const nav = navigator as unknown as {
            getAutoplayPolicy?: (arg: unknown) => string | undefined;
          };
          const policy = nav?.getAutoplayPolicy
            ? nav.getAutoplayPolicy(node) ??
              nav.getAutoplayPolicy('mediaelement')
            : undefined;
          if (policy === 'allowed-muted' && !node.muted) {
            node.muted = true;
          }
          const autoplayAllowed = getEffectiveAutoplayAllowed(node);
          const canAutoplay = autoplayAllowed;
          setAutoplayBlocked(!canAutoplay);
          const shouldGeneratePoster = isSlowConnection || !canAutoplay;

          // If video was playing before pause and autoplay is allowed, resume it
          if (wasPlayingBeforePause && canAutoplay && !completedOnce) {
            const shouldRestart = wasEndedBeforePause;
            wasEndedBeforePause = false;
            wasPlayingBeforePause = false;
            playVideo(node, shouldRestart);
            return;
          }

          // On fast connections, attempt to generate a dynamic poster from the last frame.
          // JPEG + smaller canvas + idle callback to keep toDataURL fast and off the critical path.
          if (shouldGeneratePoster && !posterRequested && !computedPoster) {
            setPosterRequested(true);
            const el = node;
            const canAutoplayForPoster = canAutoplay;
            const makePoster = () => {
              const vw = el.videoWidth || 0;
              const vh = el.videoHeight || 0;
              if (!vw || !vh) {
                playVideo(el, wasEndedBeforePause);
                return;
              }
              const targetW = Math.min(480, vw);
              const targetH = Math.round((targetW / vw) * vh);
              const canvas = document.createElement('canvas');
              canvas.width = targetW;
              canvas.height = targetH;
              const ctx = canvas.getContext('2d');
              if (!ctx) {
                playVideo(el, wasEndedBeforePause);
                return;
              }
              const onSeeked = () => {
                if (cancelled) {
                  el.removeEventListener('seeked', onSeeked);
                  return;
                }
                const runEncode = () => {
                  if (cancelled) return;
                  try {
                    ctx.drawImage(el, 0, 0, targetW, targetH);
                    const dataUrl = canvas.toDataURL('image/jpeg', 0.6);
                    if (dataUrl && typeof dataUrl === 'string') {
                      setComputedPoster(dataUrl);
                    }
                  } catch {
                    // Ignore canvas taint or unsupported toDataURL
                  } finally {
                    el.removeEventListener('seeked', onSeeked);
                    const onResetSeeked = () => {
                      el.removeEventListener('seeked', onResetSeeked);
                      if (cancelled) return;
                      if (canAutoplayForPoster && !completedOnce) {
                        playVideo(el, false);
                      }
                    };
                    el.addEventListener('seeked', onResetSeeked, {
                      once: true
                    });
                    if (!cancelled) {
                      el.currentTime = 0;
                    }
                  }
                };
                if (typeof requestIdleCallback !== 'undefined') {
                  idleCbHandle = requestIdleCallback(runEncode, {
                    timeout: 500
                  });
                } else {
                  timeoutHandle = setTimeout(runEncode, 0);
                }
              };
              el.addEventListener('seeked', onSeeked);
              const endTime = Math.max(0, (el.duration || 0) - 0.1);
              el.currentTime = endTime;
            };
            if (el.readyState >= 1) {
              makePoster();
            } else {
              pendingMetadataEl = el;
              pendingMetadataFn = makePoster;
              el.addEventListener('loadedmetadata', makePoster, { once: true });
            }
          } else if (canAutoplay && !completedOnce && !wasPlayingBeforePause) {
            // If allowed to autoplay and not generating poster, and wasn't playing before, start from beginning
            wasEndedBeforePause = false;
            playVideo(node, false);
          } else {
            // Autoplay not allowed or was already playing; ensure we don't keep stale flags
            wasEndedBeforePause = false;
            wasPlayingBeforePause = false;
          }
        } else {
          // Store if video ended or was playing before pausing
          wasEndedBeforePause = node.ended || isEnded;
          wasPlayingBeforePause = !node.paused && !node.ended;
          node.pause();
        }
      }
    };

    const requestUpdate = () => {
      if (!ticking) {
        ticking = true;
        rafId = window.requestAnimationFrame(update);
      }
    };

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    document.addEventListener('visibilitychange', requestUpdate);
    const onNodePlay = () => {
      setIsPlaying(true);
      setIsEnded(false);
      setHasPlayedOnce(true);
    };
    const onNodePause = () => {
      setIsPlaying(false);
    };
    const onNodeEnded = () => {
      setIsEnded(true);
      setIsPlaying(false);
      setCompletedOnce(true);
    };
    node.addEventListener('play', onNodePlay);
    node.addEventListener('pause', onNodePause);
    node.addEventListener('ended', onNodeEnded);
    // Initial check
    requestUpdate();

    return () => {
      cancelled = true;
      window.removeEventListener('scroll', requestUpdate as EventListener);
      window.removeEventListener('resize', requestUpdate as EventListener);
      document.removeEventListener(
        'visibilitychange',
        requestUpdate as EventListener
      );
      if (rafId) cancelAnimationFrame(rafId);
      if (
        idleCbHandle !== undefined &&
        typeof cancelIdleCallback !== 'undefined'
      ) {
        cancelIdleCallback(idleCbHandle);
      }
      if (timeoutHandle !== undefined) {
        clearTimeout(timeoutHandle);
      }
      if (pendingMetadataFn && pendingMetadataEl) {
        pendingMetadataEl.removeEventListener(
          'loadedmetadata',
          pendingMetadataFn
        );
      }
      node.removeEventListener('play', onNodePlay);
      node.removeEventListener('pause', onNodePause);
      node.removeEventListener('ended', onNodeEnded);
    };
  }, [
    isVideo,
    muted,
    networkInfo.effectiveType,
    networkInfo.saveData,
    completedOnce,
    disableViewportManagement
  ]);

  // Poll autoplay policy every 3s to react to runtime changes (e.g., Low Power Mode toggles)
  useEffect(() => {
    if (!isVideo) return;
    if (typeof window === 'undefined') return;
    const node = videoRef.current;
    if (!node) return;

    const isElementInViewport = () => {
      const rect = node.getBoundingClientRect();
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight;
      if (rect.height === 0) return false;
      const top = Math.max(0, rect.top);
      const bottom = Math.min(viewportHeight, rect.bottom);
      const visibleHeight = Math.max(0, bottom - top);
      const ratio = visibleHeight / rect.height;
      return ratio >= 0.15;
    };

    const checkPolicy = () => {
      const nav = navigator as unknown as {
        getAutoplayPolicy?: (arg: unknown) => string | undefined;
      };
      const nextPolicy = nav?.getAutoplayPolicy
        ? nav.getAutoplayPolicy(node) ?? nav.getAutoplayPolicy('mediaelement')
        : undefined;
      const effective = nextPolicy === 'disallowed' ? 'disallowed' : 'allowed';
      if (effective === autoplayPolicyRef.current) return;
      autoplayPolicyRef.current = effective;

      if (effective === 'disallowed') {
        setAutoplayBlocked(true);
        node.pause();
        return;
      }

      if (effective === 'allowed') {
        setAutoplayBlocked(false);
        if (isElementInViewport() && !completedOnce) {
          if (!node.muted) {
            node.muted = true;
          }
          node.play().catch((err: DOMException) => {
            if (err.name === 'NotAllowedError') {
              setAutoplayBlocked(true);
              trackRumEvent('autoplay_blocked', {
                page_url: window.location.href,
                component: 'Asset',
                video_src: sourceUrl,
                muted: node.muted
              });
            }
            captureError(err);
          });
        }
      }
    };

    const id = window.setInterval(checkPolicy, 3000);
    // Run once immediately so we respond quickly after mount/source changes
    checkPolicy();
    return () => {
      window.clearInterval(id);
    };
  }, [isVideo, isSlowConnection, sourceUrl, completedOnce]);

  // Track when video is ready, optimized for LCP
  useEffect(() => {
    if (!isVideo) return;
    const node = videoRef.current;
    if (!node) return;

    // Reset videoReady when source changes
    setVideoReady(false);

    const checkVideoReady = () => {
      // Show video as soon as we have metadata (HAVE_METADATA = 1) for LCP optimization
      // Only show if we're at the beginning to avoid showing a frame from the middle
      if (node.readyState >= 1 && Math.abs(node.currentTime) < 0.1) {
        setVideoReady(true);
        return true;
      }
      return false;
    };

    // Check immediately if already loaded
    if (node.readyState >= 1) {
      checkVideoReady();
      return;
    }

    // Listen for when metadata is available (fires earlier than loadeddata)
    const onLoadedMetadata = () => {
      if (Math.abs(node.currentTime) < 0.1) {
        setVideoReady(true);
      }
    };
    // Also listen for loadeddata as fallback
    const onLoadedData = () => {
      if (node.readyState >= 1 && Math.abs(node.currentTime) < 0.1) {
        setVideoReady(true);
      }
    };
    // Also check on seeked to catch when we reset to beginning after poster generation
    const onSeeked = () => {
      if (node.readyState >= 1 && Math.abs(node.currentTime) < 0.1) {
        setVideoReady(true);
      }
    };

    node.addEventListener('loadedmetadata', onLoadedMetadata);
    node.addEventListener('loadeddata', onLoadedData);
    node.addEventListener('seeked', onSeeked);

    return () => {
      node.removeEventListener('loadedmetadata', onLoadedMetadata);
      node.removeEventListener('loadeddata', onLoadedData);
      node.removeEventListener('seeked', onSeeked);
    };
  }, [isVideo, sourceUrl]);

  if (!asset) return null;

  if (isVideo) {
    // Only show backup poster when we actually want a poster (slow or blocked) and we couldn't compute last frame
    const shouldShowBackupPoster = autoplayBlocked || isSlowConnection;
    const finalPoster =
      computedPoster ?? (shouldShowBackupPoster ? posterUrl : undefined);
    const showPosterOverlay =
      !!finalPoster &&
      (autoplayBlocked || isSlowConnection) &&
      (!hasPlayedOnce || isEnded);
    // Always set native poster when available so LCP is measured when poster is shown (video LCP optimization)
    const posterAttr = computedPoster ?? posterUrl;
    return (
      <div className={`relative ${className ?? ''}`} style={style}>
        <MediaControlButton
          visible={autoplayBlocked || isSlowConnection}
          isPlaying={isPlaying}
          isEnded={isEnded}
          position="bottom-right"
          onClick={async () => {
            const el = videoRef.current;
            if (!el) return;
            try {
              if (isPlaying) {
                el.pause();
              } else {
                if (isEnded) el.currentTime = 0;
                await el.play();
                onPlay?.();
              }
            } catch (err) {
              captureError(err as unknown as Error);
            }
          }}
        />

        {/* Poster image serves as background for the play button when autoplay is blocked/slow.
            Show before first play and again after completion. */}
        {showPosterOverlay && (
          <div
            className={`absolute inset-0 transition-opacity duration-300 ${
              !hasPlayedOnce || isEnded ? 'opacity-100' : 'opacity-0'
            } pointer-events-none`}
            style={{
              backgroundImage: `url(${finalPoster})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              zIndex: !hasPlayedOnce || isEnded ? 1 : 0
            }}
            aria-hidden="true"
          />
        )}

        <video
          ref={(el) => {
            videoRef.current = el;
            onVideoRef?.(el);
          }}
          className={cx(
            'h-full w-full transition-opacity duration-300',
            objectFit === 'contain' ? 'object-contain' : 'object-cover',
            videoReady ? 'opacity-100' : 'opacity-0'
          )}
          muted={muted}
          playsInline
          loop={loop}
          controls={controls}
          preload={
            networkInfo.saveData ||
            networkInfo.effectiveType === 'slow-2g' ||
            networkInfo.effectiveType === '2g' ||
            networkInfo.effectiveType === '3g'
              ? 'none'
              : 'metadata'
          }
          {...(loading ? { loading } : {})}
          crossOrigin="anonymous"
          onPlay={onPlay}
          poster={posterAttr}
        >
          <source src={sourceUrl} type={contentType} />
          <track kind="captions" />
          Your browser does not support the video tag.
        </video>
      </div>
    );
  }

  const imageData = getImageData(asset);
  const isSvg = contentType?.includes('svg');
  const lqipUrl = getLqipUrl(imageData.src);

  return (
    <div
      className={`relative overflow-hidden ${className ?? ''}`}
      style={style}
    >
      {/* Skip LQIP for SVGs - they're typically small and load quickly */}
      {!imageLoaded && !isSvg && (
        <div
          className="bg-gray-100 absolute inset-0"
          style={{
            backgroundImage: `url(${lqipUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(12px)',
            transform: 'scale(1.05)'
          }}
          aria-hidden="true"
        />
      )}
      <Image
        className={cx(
          !fill && 'mx-auto',
          'h-full w-full max-w-full',
          isSvg && 'opacity-100',
          !isSvg && 'transition-opacity duration-300',
          !isSvg && imageLoaded && 'opacity-100',
          !isSvg && !imageLoaded && 'opacity-0'
        )}
        loader={resolvedImageLoader}
        loading={loading}
        priority={loading === 'eager'}
        fetchPriority={loading === 'eager' ? 'high' : 'auto'}
        {...(fill
          ? {
              src: imageData.src,
              fill: true as const,
              sizes: sizesProp ?? '100vw',
              style: { objectFit }
            }
          : imageData)}
        alt={altText}
        sizes={
          sizesProp ??
          (fill
            ? '100vw'
            : '(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 800px')
        }
        onLoad={() => setImageLoaded(true)}
      />
    </div>
  );
};
